/**
 * Card-kit contracts + pure derivations (TT-51, plan §2.1) — the DTOs the
 * reusable dashboard components render. Plain client-safe module (no db, no
 * .server imports): the section resource routes (TT-52/53) build these rows
 * server-side; components never see raw rows. The kit is the reuse backbone
 * for the Action Center sections, the pipeline cards (TT-55), and the future
 * Applications Listing v2 (TT-38).
 */

/**
 * ─── Member chip design matrix (THE single source — colors + conditions) ───
 * Client "Status Circles" legend (2026-08, supersedes the 2026-07-08 legend
 * below the fold in MemberChip.tsx history): ONE ring per chip, not two —
 * the amber "guarantor recommended" OUTER ring is retired outright, and the
 * inner ring now reads as a workflow-STAGE signal, applied the SAME way to
 * every applicant type including guarantors ("apply to all type of
 * applicants" — guarantors no longer get a fixed amber identity ring).
 * Everything is derived by the pure `deriveMemberChipVisual`; MemberChip.tsx
 * renders from that descriptor, so changing a color/condition HERE updates
 * every surface (dashboard cards, pipeline, listing rows).
 *
 * Ring (workflow stage — applicant AND guarantor alike):
 *   gray    not started — added/invited, hasn't begun (kind 'invited' too)
 *   blue    in progress — started, within the 15-minute grace window
 *   amber   requested information — this application's own outstanding ask
 *           (additionalAccountRequested on its verify-income step)
 *   red     missing information — started, past the 15-minute grace window,
 *           still not finished and nothing's been asked for
 *   green   completed / ready for review — reached a finished state
 *           (submitted / processing / completed) or on billing hold
 *
 * DECIDED applications (accepted / declined / withdrawn / abandoned) keep
 * their own outcome ring so it never contradicts the corner decision badge
 * sitting on top of it — the legend above only redefines the LIVE workflow
 * states:
 *   accepted            green ring + circle (applicant) / shield (guarantor) ✓
 *   declined             red ring + circle (applicant) / shield (guarantor) ✗
 *   withdrawn/abandoned  gray ring, no badge
 *
 * CHILD    green ring + face avatar, always (no screening state, no trail).
 * INVITED  gray ring + initials (added, hasn't started), no trail.
 */

export type MemberChipTone = 'green' | 'blue' | 'amber' | 'red' | 'gray'

/** Tone → avatar ring/background/text classes (chip visual source). */
export const MEMBER_CHIP_RING_CLASS: Record<MemberChipTone, string> = {
	green: 'border-[#16A34A] bg-[#F0FDF4]',
	blue: 'border-[#2563EB] bg-[#EFF6FF]',
	amber: 'border-[#F59F0A] bg-[#FFFCF8]',
	red: 'border-[#DC2634] bg-[#DC26340D]',
	gray: 'border-[#F0F0F0] bg-[#FBFDFD]',
}

/** Corner decision badge — shape (circle = applicant, shield = guarantor) and
 * colors. The glyph (check / x) is drawn by the component from `mark`. */
export type MemberChipBadgeType =
	| 'applicant_accepted'
	| 'applicant_declined'
	| 'guarantor_accepted'
	| 'guarantor_declined'

export interface MemberChipBadgeDef {
	shape: 'circle' | 'shield'
	mark: 'check' | 'x'
	/** Border + icon color classes (white fill in the component). */
	className: string
}

export const MEMBER_CHIP_BADGE: Record<
	MemberChipBadgeType,
	MemberChipBadgeDef
> = {
	applicant_accepted: {
		shape: 'circle',
		mark: 'check',
		className: 'border-white fill-[#16A34A] text-white',
	},
	applicant_declined: {
		shape: 'circle',
		mark: 'x',
		className: 'border-white fill-[#DC2634] text-white',
	},
	guarantor_accepted: {
		shape: 'shield',
		mark: 'check',
		className: 'border-white fill-[#16A34A] text-white',
	},
	guarantor_declined: {
		shape: 'shield',
		mark: 'x',
		className: 'border-white fill-[#DC2634] text-white',
	},
}

export type StepPillStatus = 'completed' | 'failed' | 'pending'

/** One pill of the hover trail (design: "ID ···· Details ···· Income"). */
export interface MemberStepPill {
	key: string
	label: string
	status: StepPillStatus
}

/** Who the chip represents — drives the avatar/badge family (see the matrix
 * above). Applicant = primary/co-applicant; the others are self-describing. */
export type MemberChipKind = 'applicant' | 'guarantor' | 'child' | 'invited'

/** Screening tier of an applicant's application. The tooltip shows it so the
 * tier is visible wherever this chip renders (dashboard, listing, report). */
export type MemberScreeningType = 'full' | 'pre_screening'

/** The ONE label mapping for the screening tier — single source, so every
 * chip surface reads it the same. */
export const SCREENING_TYPE_LABEL: Record<MemberScreeningType, string> = {
	full: 'Full Screening',
	pre_screening: 'Pre-Screening',
}

export interface DashboardMemberChipData {
	key: string
	/** Compact chip label ("Tommy S"). */
	shortName: string
	/** Hover-trail headline ("Tommy Smith"). */
	fullName: string
	initials: string
	roleLabel: string // 'Primary' | 'Co-applicant' | 'Guarantor' | 'Child' | 'Invited'
	kind: MemberChipKind
	/** Raw application status; children/invited ignore it (no application row). */
	status: string
	/** Applicant has begun screening — false + incomplete = "added, not begun"
	 * (gray). Ignored for guarantor/child/invited. */
	hasStarted: boolean
	/** Screening tier of the applicant's application ('full' | 'pre_screening')
	 * → the tooltip's screening-type line. null for guarantor/child/invited
	 * (no applicant screening tier to state). */
	screeningType: MemberScreeningType | null
	/** When this application was created — the ring's blue/red split measures
	 * elapsed time from here (15-minute grace). null for child/invited (no
	 * application row). */
	startedAt: string | null
	/** This application's own outstanding ask (additionalAccountRequested on
	 * its verify-income step). false for child/invited. */
	infoRequested: boolean
	/** Billing-held submission (TT-12) — the applicant IS done even though
	 * `status` stays 'in_progress' by design; counts as green like a finished
	 * status. false for child/invited. */
	billingHeld: boolean
	/** Hover step trail; [] renders no tooltip body (children/invited). */
	steps: MemberStepPill[]
	/** Dialable number ("+15551234567") for Reach Out (tel:/sms:); null when
	 * the member has no account phone (children/invited pseudo-members). */
	phone: string | null
}

/**
 * Individual detail-page link for a member chip (2026-08): only applicant
 * and guarantor chips back a real screening application — `key` is the
 * application id for those two kinds (card-assembly's `key: member.id`);
 * child chips key off a synthetic dedupe string and invited chips off the
 * invitation id, neither of which opens a detail page, so both render null
 * (the caller falls back to the card's own combined-report click). Guarantor
 * ids resolve fine too — the /v2 route renders their separate report from
 * the same `application_type` field (load-application-detail.server.ts).
 * Links straight to /v2, same rule as the card's own href (card-assembly):
 * the bare v1 URL always 302s there, costing a full extra auth+loader pass.
 */
export function deriveMemberDetailHref(
	propertyId: string,
	member: Pick<DashboardMemberChipData, 'kind' | 'key'>,
): string | null {
	if (member.kind !== 'applicant' && member.kind !== 'guarantor') return null
	return `/properties/${propertyId}/applications/${member.key}/v2`
}

/** The visual descriptor a chip renders — the ONE derivation the component
 * consumes. Pure and total over the live status vocabulary. */
export interface MemberChipVisual {
	ringTone: MemberChipTone
	avatar: 'initials' | 'face'
	badge: MemberChipBadgeType | null
}

/** An application reached a finished state — the SAME set the individual
 * pipeline's `deriveIndividualPipelineColumn` (individual-pipeline-kit) uses
 * for its "ready for decision" column; duplicated locally rather than
 * imported since card-kit is the lower-level module the pipeline kit itself
 * builds on (see file header). */
const FINISHED_STATUSES: ReadonlySet<string> = new Set([
	'submitted',
	'processing',
	'completed',
])

/** "Missing information" only kicks in once a started-but-unfinished
 * application has sat for 15 minutes — the "Status Circles" legend's grace
 * window, giving a just-started applicant a blue window before the ring
 * reads as a problem. */
const MISSING_INFO_GRACE_MS = 15 * 60 * 1000

export function deriveMemberChipVisual(
	input: Pick<
		DashboardMemberChipData,
		| 'kind'
		| 'status'
		| 'hasStarted'
		| 'infoRequested'
		| 'startedAt'
		| 'billingHeld'
	>,
	/** Explicit "now" so elapsed-time comparison stays pure/testable —
	 * defaults to the real clock in production. */
	now: Date = new Date(),
): MemberChipVisual {
	const { kind, status } = input

	if (kind === 'child') {
		return { ringTone: 'green', avatar: 'face', badge: null }
	}
	if (kind === 'invited') {
		return { ringTone: 'gray', avatar: 'initials', badge: null }
	}

	// Decision badge — circle (applicant) or shield (guarantor); orthogonal to
	// the workflow-stage ring below.
	const badge: MemberChipBadgeType | null =
		status === 'accepted'
			? kind === 'guarantor'
				? 'guarantor_accepted'
				: 'applicant_accepted'
			: status === 'declined'
				? kind === 'guarantor'
					? 'guarantor_declined'
					: 'applicant_declined'
				: null

	let ringTone: MemberChipTone
	switch (status) {
		// Decided/closed applications keep their outcome color so the ring never
		// contradicts the badge on top of it — the legend below only redefines
		// the LIVE workflow states.
		case 'accepted':
			ringTone = 'green'
			break
		case 'declined':
			ringTone = 'red'
			break
		case 'withdrawn':
		case 'abandoned':
			ringTone = 'gray'
			break
		default:
			if (!input.hasStarted) {
				ringTone = 'gray'
			} else if (input.infoRequested) {
				ringTone = 'amber'
			} else if (FINISHED_STATUSES.has(status) || input.billingHeld) {
				ringTone = 'green'
			} else {
				const elapsedMs = input.startedAt
					? now.getTime() - new Date(input.startedAt).getTime()
					: 0
				ringTone = elapsedMs > MISSING_INFO_GRACE_MS ? 'red' : 'blue'
			}
	}
	return { ringTone, avatar: 'initials', badge }
}

/** Role pill tone of the All Applicants sheet rows (client mock 2026-07-10):
 * applicants read green, guarantors amber (their identity color everywhere),
 * children/invited muted. Pure so the mapping is testable and single-source. */
export function deriveMemberRolePillClass(kind: MemberChipKind): string {
	switch (kind) {
		case 'applicant':
			return 'bg-[#F0FDF4] text-[#16A34A]'
		case 'guarantor':
			return 'bg-[#FFF6EA] text-[#F59F0A]'
		default:
			return 'bg-[#F4F4F2] text-[#747474]'
	}
}

export interface DashboardGroupCardData {
	groupKey: string
	/** Detail-page target for the whole card's action. */
	href: string
	/** The primary's application id — the in-place timeline fetch key
	 * (`/resources/dashboard-v2/timeline`, QA 2026-07-15). */
	primaryApplicationId: string
	propertyId: string
	propertyLabel: string
	/** Street address for the QR dialog subtitle; null falls back to name. */
	propertyAddress: string | null
	/** Card thumbnail (property images[0]); null renders a placeholder. */
	imageUrl: string | null
	/** Pre-generated QR PNG data-URL for the share dialog; null = no QR yet. */
	qrDataUrl: string | null
	/** Apply-link token — the client builds `${origin}/apply/${token}`. */
	qrCodeToken: string | null
	bedLabel: string | null
	bathLabel: string | null
	unitLabel: string | null
	/** Household display score (family score if computed, else the primary's
	 * overall score — the v1 rule); null = pending. */
	score: number | null
	/** False = never render the score bar (pre-screening — the locked client
	 * decision to hide pre-screening scores). */
	showScore: boolean
	/** HOW a shown score renders — the owning org's `score_display` setting
	 * (per-landlord registry): band bar (default) or the raw NN/100. Resolved
	 * from the primary's tenant, so staff surfaces match the landlord's own. */
	scoreDisplay: 'band' | 'numeric'
	/** Household "Started on" (client 2026-09-07 — was "Applied on"): the
	 * earliest applicant's consent-row created_at (first consent; never moves
	 * on a re-consent), app created_at fallback for legacy rows (ISO).
	 * "Applied" implied a finished application. */
	submittedOn: string | null
	/** Primary's property-step move-in date (raw string) — sheet's Move-In row.
	 * null when not set. */
	moveInDate: string | null
	members: DashboardMemberChipData[]
}

/** How many member chips a card row shows before collapsing to "+N More". */
export const MEMBER_CHIP_LIMIT = 5

/** An applicant has BEGUN screening when they're past the initial working
 * state or has finished at least one TRACKED step — the gray("added, not
 * started") vs red("missing information") split (reference legend). Pass
 * only the STEP_PILL_STEP_TYPES statuses: untracked steps (property,
 * personal-info, review) never count, or a chip would read "started" for
 * someone who merely opened the application (QA 2026-07-15 — the timeline
 * chips must agree with the dashboard chips, single source here). */
export function deriveHasStarted(
	status: string,
	steps: Record<string, string> | undefined,
): boolean {
	if (status !== 'in_progress' && status !== '' && status != null) return true
	return Object.values(steps ?? {}).some((s) => s === 'completed')
}

/**
 * REAL household members among a card's chips — applicants + guarantors
 * only. `card.members` also carries CHILD and INVITED pseudo-chips (they
 * render as chips but are not applications), so any "+N members" style
 * count must go through this filter: a solo primary with one child is a
 * household of ONE, not "Name +1" (QA 2026-07-15, pipeline cards).
 */
export function countHouseholdMembers(
	members: Array<Pick<DashboardMemberChipData, 'kind'>>,
): number {
	return members.filter(
		(member) => member.kind === 'applicant' || member.kind === 'guarantor',
	).length
}

/**
 * Listing search → lead member chip. All Applications shows one chip (the
 * primary by default). When `q` matches a co-applicant or guarantor name,
 * promote that member to the front so the row features the hit. Empty /
 * whitespace search, or no applicant/guarantor name hit (e.g. property-only
 * match), leave order unchanged. Child/invited chips never promote.
 */
export function promoteSearchMatchedMembers<
	T extends Pick<DashboardMemberChipData, 'kind' | 'fullName'>,
>(members: T[], search: string | null | undefined): T[] {
	const needle = search?.trim().toLowerCase()
	if (!needle) return members
	const matched: T[] = []
	const rest: T[] = []
	for (const member of members) {
		const isScreeningMember =
			member.kind === 'applicant' || member.kind === 'guarantor'
		if (isScreeningMember && member.fullName.toLowerCase().includes(needle)) {
			matched.push(member)
		} else {
			rest.push(member)
		}
	}
	if (matched.length === 0) return members
	return [...matched, ...rest]
}

/** The screening step-types the hover trail reads. ID + Income are their own
 * steps; "Details" aggregates the employment + rental steps. Credit (TenCheck)
 * and Court Records (OpenRoom) are HIDDEN for now (client 2026-07-10) — re-add
 * their step-types here and the pills in buildStepPills to bring them back. */
export const STEP_PILL_STEP_TYPES = [
	'id-verification',
	'verify-income',
	'employment',
	'rental',
] as const

/**
 * Raw step statuses (by stepType) → the member's hover trail: exactly three
 * pills, ID · Details · Income, each GREEN when completed and RED otherwise
 * (client 2026-07-10 — two states only, no gray "pending"). "Details" is a
 * combination of the employment + rental steps: complete only when BOTH are.
 */
export function buildStepPills(
	stepStatuses: Record<string, string | undefined>,
): MemberStepPill[] {
	const done = (key: string) => stepStatuses[key] === 'completed'
	const pill = (ok: boolean): StepPillStatus => (ok ? 'completed' : 'failed')
	return [
		{
			key: 'id-verification',
			label: 'ID',
			status: pill(done('id-verification')),
		},
		{
			key: 'details',
			label: 'Details',
			status: pill(done('employment') && done('rental')),
		},
		{
			key: 'verify-income',
			label: 'Income',
			status: pill(done('verify-income')),
		},
	]
}

export function deriveSingularFactLabel(label: string | null): string | null {
	if (!label) return null
	return label.replace(/(Bed|Bath)s$/, '$1')
}

/**
 * Best display name for a household member. The account's free-text `name`
 * is frequently just a first name (self-signup captures one field), so the
 * chip's last-name initial only showed for the minority whose `name` happened
 * to carry a surname (client 2026-07-24: "shows in some cases, not others").
 * The application separately captures structured `first_name`/`last_name` on
 * the user profile — prefer whichever source actually carries a surname so
 * `deriveShortMemberName` can abbreviate to "First L" consistently, then fall
 * back to the fullest thing we have (profile first-only, account name, username).
 */
export function pickMemberFullName(input: {
	profileFirstName?: string | null
	profileLastName?: string | null
	accountName?: string | null
	username?: string | null
}): string {
	const profileName = [input.profileFirstName, input.profileLastName]
		.map((part) => part?.trim() ?? '')
		.filter(Boolean)
		.join(' ')
	const accountName = input.accountName?.trim() ?? ''
	const hasSurname = (value: string) =>
		value.split(/\s+/).filter(Boolean).length >= 2
	if (hasSurname(profileName)) return profileName
	if (hasSurname(accountName)) return accountName
	return profileName || accountName || input.username?.trim() || 'Applicant'
}

/** "Tommy Smith" → "Tommy S" (chip label of the approved design). */
export function deriveShortMemberName(fullName: string): string {
	const parts = fullName.trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) return 'Unknown'
	if (parts.length === 1) return parts[0]
	return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}`
}

export interface FollowUpReasonInput {
	bankRequested: boolean
	pendingGuarantorInvite: boolean
	pendingCoApplicantInvite: boolean
	/** A guarantor has JOINED and is still completing screening while the
	 * applicants are done — the household waits on the guarantor to finish, not
	 * on the applicant or a pending invite. */
	guarantorScreening: boolean
	/** Whole days since the household's last activity; null when the group is
	 * in the requested bucket (reason comes from the ask, not the silence). */
	stalledDays: number | null
}

/**
 * WHY a group sits in Follow-Ups (TT-53, D6) — the card's reason pill.
 * Priority mirrors the bucket derivation: landlord asks first (bank, then
 * guarantor invite), then the assembling-household invite, then a joined
 * guarantor still completing screening, then staleness.
 */
export function deriveFollowUpReason(input: FollowUpReasonInput): string {
	if (input.bankRequested) return 'Additional bank account requested'
	if (input.pendingGuarantorInvite) return 'Guarantor invitation pending'
	if (input.pendingCoApplicantInvite) return 'Co-applicant invitation pending'
	if (input.guarantorScreening) return 'Guarantor completing screening'
	if (input.stalledDays != null) {
		return `No activity for ${input.stalledDays} day${input.stalledDays === 1 ? '' : 's'}`
	}
	return 'Awaiting applicant activity'
}

function pluralizeUnit(value: number, unit: string): string {
	return `${unit}${value === 1 ? '' : 's'}`
}

/**
 * Follow-Ups card corner label (Figma 2026-08): how long since the FIRST
 * household applicant started their application (`card.submittedOn` —
 * earliest applicant consent, created_at fallback; guarantors excluded). < 1hr → minutes,
 * < 24hr → hours to one decimal, else whole days + remainder hours — the
 * exact bucketing the design calls out ("5 Mins Ago" / "1.5 Hrs Ago" /
 * "2 Days 2 Hrs Ago"). `now` is a param so this stays pure/testable; the
 * caller passes `new Date()` at render time. null input/invalid date → null
 * (caller renders nothing).
 */
export function deriveAppliedDurationLabel(
	submittedOn: string | null,
	now: Date = new Date(),
): string | null {
	if (!submittedOn) return null
	const start = new Date(submittedOn).getTime()
	if (!Number.isFinite(start)) return null

	const diffMinutes = Math.max(0, now.getTime() - start) / 60_000
	if (diffMinutes < 60) {
		const mins = Math.max(1, Math.round(diffMinutes))
		return `${mins} ${pluralizeUnit(mins, 'Min')} Ago`
	}
	const diffHours = diffMinutes / 60
	if (diffHours < 24) {
		const hours = Math.round(diffHours * 10) / 10
		return `${hours} ${pluralizeUnit(hours, 'Hr')} Ago`
	}
	const totalHours = Math.floor(diffHours)
	const days = Math.floor(totalHours / 24)
	const remainderHours = totalHours % 24
	return `${days} ${pluralizeUnit(days, 'Day')} ${remainderHours} ${pluralizeUnit(remainderHours, 'Hr')} Ago`
}

/**
 * Short pill labels for the pipeline "Requested" column — WHAT the landlord
 * asked for and FROM WHOM (pipeline decisions 2026-07-11). Distinct from
 * `deriveFollowUpReason`: compact chip labels, not the full reason sentence,
 * and every outstanding ask shows (bank asks first — mirrors the reason
 * precedence). Attribution rule: bank asks are per-application, so in a
 * MULTI-applicant group each carries its member's short name ("Tommy S ·
 * Bank"); a solo applicant's name is already the card headline, so the pill
 * stays generic. Guarantor invites aren't tied to one applicant in the data
 * model (the invite backs the household), so that pill is never attributed.
 * Empty array = nothing requested (the caller renders no extra pills).
 */
export function deriveRequestedPillLabels(input: {
	/** Short names of members with an open additional-bank ask ([] = none). */
	bankRequestedNames: string[]
	guarantorRequested: boolean
	/** Adult applicants in the group (guarantors excluded) — 1 = solo. */
	applicantCount: number
}): string[] {
	const labels =
		input.applicantCount > 1
			? input.bankRequestedNames.map((name) => `${name} · Bank`)
			: input.bankRequestedNames.map(() => 'Additional Bank Acc')
	if (input.guarantorRequested) labels.push('Guarantor')
	return labels
}

/**
 * The household display score shown by the score band bar (dashboard + listing
 * share this via assembleGroupCards). Rules:
 *  - a COMPLETED family score always wins (the real household verdict);
 *  - a SOLO applicant (no co-applicants) has no family verdict, so their own
 *    overall score IS the household score;
 *  - a MULTI-applicant group with no family score yet is PENDING (null) — we
 *    must never borrow the primary's individual score as a stand-in for a
 *    family verdict that doesn't exist (client 2026-07-09).
 * `applicantCount` counts adults only (guarantors excluded). null/undefined
 * scores mean "not computed"; the caller renders null as a faded/pending bar.
 */
export function deriveHouseholdScore(input: {
	familyScore: number | null | undefined
	applicantCount: number
	primaryOverallScore: number | null | undefined
}): number | null {
	if (typeof input.familyScore === 'number')
		return Math.round(input.familyScore)
	if (input.applicantCount <= 1) return input.primaryOverallScore ?? null
	return null
}
